import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { Volume2, VolumeX, Mic, Music, AudioWaveform, MessageSquare, Layers, Film, ZoomIn, ZoomOut, Maximize, Magnet, X, Plus } from "lucide-react";
import { getFrameImageUrl } from "@/services/shotService";
import { groupsFromEdl } from "./reelTiming";
import { computePeaks, drawWaveform } from "./waveform";

const PX_PER_SEC = 64;
const LABEL_W = 104;
const VIDEO_H = 54;
const AUDIO_H = 46;

const TRACK_META = {
  vo: { color: "#60a5fa", Icon: Mic, label: "Voice" },
  music: { color: "#34d399", Icon: Music, label: "Music" },
  sfx: { color: "#f59e0b", Icon: AudioWaveform, label: "SFX" },
  overlay: { color: "#a78bfa", Icon: Layers, label: "Overlay" },
};

/** One audio clip block: draggable body + trim handles + waveform. */
function AudioClip({ track, clip, engine, pxPerSec, selected, onSelect, onChange, doSnap }) {
  const canvasRef = useRef(null);
  const meta = TRACK_META[track.kind] || { color: "#9ca3af" };
  const w = Math.max(8, clip.duration * pxPerSec);

  useEffect(() => {
    const buf = engine?.getBuffer(clip.id);
    if (!buf || !canvasRef.current) return;
    const cv = canvasRef.current;
    cv.width = Math.max(2, Math.floor(w));
    cv.height = AUDIO_H - 14;
    drawWaveform(cv, computePeaks(buf, Math.min(400, Math.floor(w))), "rgba(255,255,255,0.45)");
  }, [clip.id, w, engine]);

  const startDrag = (e, mode) => {
    e.stopPropagation();
    onSelect(clip.id);
    const base = { ...clip };
    const sn = (v) => (doSnap ? doSnap(v) : v);
    const startX = e.clientX;
    const move = (ev) => {
      const d = (ev.clientX - startX) / pxPerSec;
      if (mode === "move") onChange(track.id, clip.id, { start: Math.max(0, sn(base.start + d)) }, false);
      else if (mode === "l") {
        const ns = Math.max(0, sn(base.start + d));
        const nd = base.duration - (ns - base.start);
        if (nd > 0.1) onChange(track.id, clip.id, { start: ns, in: (base.in || 0) + (ns - base.start), duration: nd }, false);
      } else if (mode === "r") {
        const edge = sn(base.start + base.duration + d);
        onChange(track.id, clip.id, { duration: Math.max(0.1, edge - base.start) }, false);
      }
    };
    const up = () => { onChange(track.id, clip.id, {}, true); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div className="absolute top-1 bottom-1 rounded-md overflow-hidden cursor-grab active:cursor-grabbing select-none"
      style={{ left: clip.start * pxPerSec, width: w, background: `${meta.color}22`, opacity: clip.disabled ? 0.4 : 1,
        border: `1px solid ${selected ? meta.color : meta.color + "66"}`, boxShadow: selected ? `0 0 0 1px ${meta.color}` : "none" }}
      onPointerDown={(e) => startDrag(e, "move")}>
      <canvas ref={canvasRef} className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none" style={{ height: AUDIO_H - 14 }} />
      <span className="absolute left-1 top-0.5 text-[8px] text-white/80 pointer-events-none truncate" style={{ maxWidth: w - 8 }}>{clip.label || clip.group_key || "clip"}</span>
      <div onPointerDown={(e) => startDrag(e, "l")} className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize" style={{ background: meta.color }} />
      <div onPointerDown={(e) => startDrag(e, "r")} className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize" style={{ background: meta.color }} />
    </div>
  );
}

/** Multi-track timeline: a video reference row + editable audio stem tracks,
 *  a draggable playhead, click-to-seek, per-track volume/mute. */
export default function MultiTrackTimeline({ edl, engine, fps, playhead, shots, marks,
  onSeek, onSelectShot, selectedShotKey, selectedClipId, onSelectClip, onChangeClip, onChangeTrack, onReorderShots, onDropMedia, onTrimVideo, onRemoveLayer, onDropToLayer, onPickTransition, onAddVideoTrack, onAddAudioTrack }) {
  const laneRef = useRef(null);
  const [dropActive, setDropActive] = useState(false);
  const duration = engine?.duration || edl?.totalSec || 1;
  const [pxPerSec, setPxPerSec] = useState(PX_PER_SEC);
  const [magnet, setMagnet] = useState(true);
  const snapTargets = useMemo(() => {
    const s = new Set([0]);
    (edl?.tracks || []).forEach((t) => (t.clips || []).forEach((c) => { s.add(Math.round(c.start * 1000) / 1000); s.add(Math.round((c.start + c.duration) * 1000) / 1000); }));
    return [...s];
  }, [edl]);
  const snapSec = useCallback((sec, extra = []) => {
    if (!magnet) return sec;
    const thr = 8 / pxPerSec;
    let best = sec, bd = thr;
    for (const t of snapTargets) { const dd = Math.abs(t - sec); if (dd < bd) { bd = dd; best = t; } }
    for (const t of extra) { const dd = Math.abs(t - sec); if (dd < bd) { bd = dd; best = t; } }
    return best;
  }, [magnet, snapTargets, pxPerSec]);
  const zoomFit = useCallback(() => {
    const lane = laneRef.current; if (!lane) return;
    setPxPerSec(Math.max(8, Math.min(240, (lane.clientWidth - 12) / Math.max(1, duration))));
  }, [duration]);
  const totalW = Math.max(200, duration * pxPerSec);
  const audioTracks = (edl?.tracks || []).filter((t) => t.kind !== "video" && t.kind !== "overlay");
  const overlayTracks = (edl?.tracks || []).filter((t) => t.kind === "overlay");
  const groups = useMemo(() => groupsFromEdl(edl, fps), [edl, fps]);
  const [, force] = useState(0);
  useEffect(() => { force((n) => n + 1); }, [engine]); // redraw waveforms once buffers load

  // ---- drag-to-reorder shots (horizontal) ----
  const [drag, setDrag] = useState(null); // { key, dx, dropIndex }
  const startShotDrag = useCallback((e, group) => {
    if (e.button !== 0) return;
    const startX = e.clientX;
    const centers = groups.map((g) => ({ key: g.group_key, c: (g.startFrame / fps) * pxPerSec + (g.durationInFrames / fps) * pxPerSec / 2 }));
    const selfCenter = centers.find((c) => c.key === group.group_key)?.c ?? 0;
    let moved = false;
    const move = (ev) => {
      const dx = ev.clientX - startX;
      if (!moved && Math.abs(dx) < 4) return;
      moved = true;
      const eff = selfCenter + dx;
      const dropIndex = centers.filter((c) => c.key !== group.group_key && c.c < eff).length;
      setDrag({ key: group.group_key, dx, dropIndex });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setDrag((d) => {
        if (!moved || !d) { onSelectShot(group); return null; }
        const keys = groups.map((g) => g.group_key).filter((k) => k !== group.group_key);
        keys.splice(d.dropIndex, 0, group.group_key);
        const cur = groups.map((g) => g.group_key);
        if (keys.some((k, i) => k !== cur[i])) onReorderShots?.(keys);
        return null;
      });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [groups, fps, pxPerSec, onSelectShot, onReorderShots]);

  // ---- drag a single-clip shot's left/right edge to trim (in/duration) ----
  const startVideoTrim = useCallback((e, group, side) => {
    e.stopPropagation();           // don't start a reorder drag
    const c = group.clips?.[0];
    if (!c || !onTrimVideo) return;
    const snapIn = c.in || 0, snapDur = c.duration, startX = e.clientX;
    const move = (ev) => {
      const d = (ev.clientX - startX) / pxPerSec;
      if (side === "r") onTrimVideo(group.group_key, { duration: Math.max(0.3, snapDur + d) }, false);
      else onTrimVideo(group.group_key, { in: Math.max(0, snapIn + d), duration: Math.max(0.3, snapDur - d) }, false);
    };
    const up = () => { onTrimVideo(group.group_key, {}, true); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [onTrimVideo, pxPerSec]);

  // x of the insertion indicator during a drag
  const dropX = useMemo(() => {
    if (!drag) return null;
    const others = groups.filter((g) => g.group_key !== drag.key);
    if (drag.dropIndex <= 0) return (groups[0]?.startFrame / fps) * pxPerSec || 0;
    const g = others[drag.dropIndex - 1];
    return g ? ((g.startFrame + g.durationInFrames) / fps) * pxPerSec : 0;
  }, [drag, groups, fps, pxPerSec]);

  const seekFromEvent = useCallback((e) => {
    const lane = laneRef.current; if (!lane) return;
    const rect = lane.getBoundingClientRect();
    const x = e.clientX - rect.left + lane.scrollLeft;
    onSeek(snapSec(Math.max(0, x / pxPerSec)));
  }, [onSeek, pxPerSec, snapSec]);

  // press-and-drag anywhere on the ruler/empty lane to scrub the playhead
  const startScrub = useCallback((e) => {
    seekFromEvent(e);
    const move = (ev) => seekFromEvent(ev);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [seekFromEvent]);

  const onLaneDrop = useCallback((e) => {
    e.preventDefault(); setDropActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file || !onDropMedia) return;
    const lane = laneRef.current;
    const rect = lane.getBoundingClientRect();
    const x = e.clientX - rect.left + lane.scrollLeft;
    onDropMedia(file, Math.max(0, x / pxPerSec));
  }, [onDropMedia, pxPerSec]);

  const playheadX = playhead * pxPerSec;
  useEffect(() => {
    const lane = laneRef.current; if (!lane) return;
    if (playheadX < lane.scrollLeft || playheadX > lane.scrollLeft + lane.clientWidth - 60) {
      lane.scrollLeft = Math.max(0, playheadX - lane.clientWidth / 2);
    }
  }, [playheadX]);

  return (
    <div className="flex border border-white/10 rounded-lg overflow-hidden bg-black/40">
      {/* left: track labels */}
      <div className="flex-shrink-0 bg-black/40 border-r border-white/10" style={{ width: LABEL_W }}>
        <div className="h-5 border-b border-white/5 flex items-center justify-center gap-1.5">
          <button onClick={() => setPxPerSec((p) => Math.max(8, p / 1.4))} title="Zoom out" className="text-white/45 hover:text-white"><ZoomOut size={11} /></button>
          <button onClick={zoomFit} title="Fit to window" className="text-white/45 hover:text-white"><Maximize size={10} /></button>
          <button onClick={() => setPxPerSec((p) => Math.min(240, p * 1.4))} title="Zoom in" className="text-white/45 hover:text-white"><ZoomIn size={11} /></button>
          <button onClick={() => setMagnet((m) => !m)} title={magnet ? "Snapping on" : "Snapping off"} className={magnet ? "text-[var(--color-gold-400)]" : "text-white/30"}><Magnet size={10} /></button>
        </div>
        <div className="flex items-center px-2 text-[10px] text-white/60" style={{ height: VIDEO_H }}>Shots</div>
        {audioTracks.map((t) => {
          const m = TRACK_META[t.kind] || { Icon: AudioWaveform, label: t.kind };
          const Icon = m.Icon;
          return (
            <div key={t.id} className="flex items-center gap-1 px-2 border-t border-white/5" style={{ height: AUDIO_H }}>
              <button onClick={() => onChangeTrack(t.id, { muted: !t.muted })} className={t.muted ? "text-white/30" : "text-white/70"}>
                {t.muted ? <VolumeX size={12} /> : <Icon size={12} style={{ color: m.color }} />}
              </button>
              <input type="range" min={0} max={1.5} step={0.05} value={t.gain ?? 1}
                onChange={(e) => onChangeTrack(t.id, { gain: parseFloat(e.target.value) })}
                className="w-12 accent-white/40" title={`${m.label} volume`} />
              <button onClick={() => onChangeTrack(t.id, { solo: !t.solo })} title="Solo this track" className={`text-[9px] font-bold ${t.solo ? "text-amber-400" : "text-white/25 hover:text-white/50"}`}>S</button>
            </div>
          );
        })}
        {overlayTracks.map((t, i) => (
          <div key={t.id} className="flex items-center justify-between gap-1 px-2 border-t border-white/5 text-[10px] text-white/60" style={{ height: AUDIO_H }}>
            <span className="flex items-center gap-1"><Layers size={12} style={{ color: TRACK_META.overlay.color }} /> V{i + 2}</span>
            {onRemoveLayer && <button onClick={() => onRemoveLayer(t.id)} title="Remove this video layer" className="text-white/30 hover:text-red-400"><X size={11} /></button>}
          </div>
        ))}
        {(onAddVideoTrack || onAddAudioTrack) && (
          <div className="border-t border-white/10 px-2 py-1.5 space-y-1.5">
            {onAddVideoTrack && (
              <button onClick={onAddVideoTrack} title="Add a stacked video layer" className="flex items-center gap-1 w-full text-[10px] text-white/40 hover:text-white"><Plus size={11} /> Video track</button>
            )}
            {onAddAudioTrack && (
              <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                <Plus size={11} /><span>Audio:</span>
                {[["vo", "Voice"], ["music", "Music"], ["sfx", "SFX"]].map(([k, lbl]) => (
                  <button key={k} onClick={() => onAddAudioTrack(k)} className="px-1 rounded hover:bg-white/10 hover:text-white" style={{ color: TRACK_META[k]?.color }}>{lbl}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* right: scrollable lanes */}
      <div ref={laneRef} className={`relative flex-1 overflow-x-auto overflow-y-hidden ${dropActive ? "ring-2 ring-sky-400/70 ring-inset" : ""}`}
        onDragOver={onDropMedia ? (e) => { e.preventDefault(); if (!dropActive) setDropActive(true); } : undefined}
        onDragLeave={onDropMedia ? (e) => { if (e.currentTarget === e.target) setDropActive(false); } : undefined}
        onDrop={onDropMedia ? onLaneDrop : undefined}>
        <div className="relative" style={{ width: totalW }}>
          {/* ruler */}
          <div className="h-5 border-b border-white/10 relative cursor-pointer" onPointerDown={startScrub}>
            {Array.from({ length: Math.ceil(duration) + 1 }).map((_, s) => (
              <div key={s} className="absolute top-0 bottom-0 border-l border-white/10" style={{ left: s * pxPerSec }}>
                <span className="text-[8px] text-white/40 ml-0.5">{s}s</span>
              </div>
            ))}
            {(edl?.markers || []).map((mk, i) => (
              <div key={`mk${i}`} className="absolute top-0.5 pointer-events-none z-10" title={`Marker @ ${mk.toFixed(1)}s (M to toggle)`}
                style={{ left: mk * pxPerSec - 3, width: 0, height: 0, borderLeft: "3px solid transparent", borderRight: "3px solid transparent", borderTop: "5px solid #f472b6" }} />
            ))}
          </div>

          {/* video reference row (drag a shot to reorder) */}
          <div className="relative" style={{ height: VIDEO_H }} onPointerDown={(e) => { if (e.target === e.currentTarget) seekFromEvent(e); }}>
            {groups.map((g) => {
              const x = (g.startFrame / fps) * pxPerSec;
              const w = (g.durationInFrames / fps) * pxPerSec;
              const lead = g.lead;
              const mark = marks?.[lead.frame_id]?.review_status ?? lead.review_status;
              const dragging = drag?.key === g.group_key;
              return (
                <button key={g.group_key} onPointerDown={(e) => { e.stopPropagation(); startShotDrag(e, g); }} title="Click to select · drag to reorder"
                  className={`absolute top-1 bottom-1 rounded overflow-hidden border cursor-grab active:cursor-grabbing select-none ${selectedShotKey === g.group_key ? "border-[var(--color-gold-400)]" : "border-white/10"} ${dragging ? "ring-2 ring-[var(--color-gold-400)]" : ""}`}
                  style={{ left: x, width: Math.max(6, w), transform: dragging ? `translateX(${drag.dx}px)` : undefined,
                    zIndex: dragging ? 30 : 1, opacity: dragging ? 0.85 : 1, transition: dragging ? "none" : "left 0.15s ease" }}>
                  {lead.mediaType === "video"
                    ? (lead.poster
                        ? <img src={lead.poster} alt="" className="w-full h-full object-cover opacity-80 pointer-events-none" draggable={false} />
                        : <div className="w-full h-full flex items-center justify-center bg-violet-900/40"><Film size={14} className="text-violet-300" /></div>)
                    : <img src={lead.src || getFrameImageUrl(lead.frame_id)} alt="" className="w-full h-full object-cover opacity-80 pointer-events-none" loading="lazy" draggable={false} />}
                  {lead.mediaType === "video" && <Film size={7} className="absolute bottom-0.5 right-0.5 text-violet-200" />}
                  {g.vo_text && <MessageSquare size={7} className={`absolute bottom-0.5 left-0.5 ${g.has_audio ? "text-emerald-400" : "text-white/60"}`} />}
                  {mark && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full" style={{ background: { approved: "#34d399", "needs-work": "#fbbf24", flagged: "#f87171" }[mark] }} />}
                  {g.clips?.length === 1 && onTrimVideo && (
                    <>
                      <div onPointerDown={(e) => startVideoTrim(e, g, "l")} title="Trim start" className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-[rgba(232,197,110,0.8)] hover:bg-[var(--color-gold-400)] z-10" />
                      <div onPointerDown={(e) => startVideoTrim(e, g, "r")} title="Trim end" className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-[rgba(232,197,110,0.8)] hover:bg-[var(--color-gold-400)] z-10" />
                    </>
                  )}
                </button>
              );
            })}
            {/* transition chips at each cut (boundary entering a shot) */}
            {onPickTransition && groups.map((g, gi) => {
              if (gi === 0) return null;
              const x = (g.startFrame / fps) * pxPerSec;
              const tr = g.clips?.[0]?.transition_in;
              return (
                <button key={`tr-${g.group_key}`} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); onPickTransition(g.group_key); }}
                  title={tr ? `Transition: ${tr.type} (${(tr.duration || 0).toFixed(1)}s) — click to change` : "Add a transition"}
                  className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-20 w-4 h-4 rounded-full flex items-center justify-center text-[8px] leading-none border ${tr ? "bg-[var(--color-gold-500)] border-[var(--color-gold-300)] text-black" : "bg-black/70 border-white/30 text-white/70 hover:border-white/60"}`}
                  style={{ left: x }}>{tr ? "▸" : "+"}</button>
              );
            })}
            {drag && dropX != null && (
              <div className="absolute top-0 bottom-0 w-[3px] bg-[var(--color-gold-400)] rounded pointer-events-none z-40" style={{ left: dropX - 1, boxShadow: "0 0 6px rgba(245,196,83,0.9)" }} />
            )}
          </div>

          {/* audio stem tracks */}
          {audioTracks.map((t) => (
            <div key={t.id} className="relative border-t border-white/5" style={{ height: AUDIO_H }}
              onPointerDown={(e) => { if (e.target === e.currentTarget) seekFromEvent(e); }}>
              {t.clips.map((c) => (
                <AudioClip key={c.id} track={t} clip={c} engine={engine} pxPerSec={pxPerSec}
                  selected={selectedClipId === c.id} onSelect={onSelectClip} onChange={onChangeClip} doSnap={(sec) => snapSec(sec, [playhead])} />
              ))}
            </div>
          ))}

          {/* overlay / stacked video layers (image/video composited over the base) */}
          {overlayTracks.map((t) => (
            <div key={t.id} className="relative border-t border-white/5" style={{ height: AUDIO_H }}
              onPointerDown={(e) => { if (e.target === e.currentTarget) seekFromEvent(e); }}
              onDragOver={onDropToLayer ? (e) => e.preventDefault() : undefined}
              onDrop={onDropToLayer ? (e) => {
                e.preventDefault();
                const lane = laneRef.current; if (!lane) return;
                const rect = lane.getBoundingClientRect();
                const atSec = Math.max(0, (e.clientX - rect.left + lane.scrollLeft) / pxPerSec);
                const aj = e.dataTransfer.getData("application/x-reel-asset");
                const file = e.dataTransfer?.files?.[0];
                if (aj) { try { onDropToLayer(t.id, { asset: JSON.parse(aj) }, atSec); } catch { /* bad payload */ } }
                else if (file) onDropToLayer(t.id, { file }, atSec);
              } : undefined}>
              {t.clips.map((c) => (
                <AudioClip key={c.id} track={t} clip={c} engine={engine} pxPerSec={pxPerSec}
                  selected={selectedClipId === c.id} onSelect={onSelectClip} onChange={onChangeClip} doSnap={(sec) => snapSec(sec, [playhead])} />
              ))}
            </div>
          ))}

          {/* in/out export range (I/O keys) — dim what won't render, mark the edges */}
          {(() => {
            const ri = edl?.range_in || 0; const ro = edl?.range_out;
            if (!ri && (ro == null)) return null;
            const riX = ri * pxPerSec; const roX = (ro != null ? ro : duration) * pxPerSec;
            return (
              <>
                {ri > 0 && <div className="absolute top-5 bottom-0 bg-black/55 pointer-events-none z-10" style={{ left: 0, width: riX }} />}
                {ro != null && <div className="absolute top-5 bottom-0 bg-black/55 pointer-events-none z-10" style={{ left: roX, width: Math.max(0, totalW - roX) }} />}
                <div className="absolute top-0 bottom-0 w-[2px] bg-sky-400 pointer-events-none z-10" style={{ left: riX }} />
                <div className="absolute top-0 bottom-0 w-[2px] bg-sky-400 pointer-events-none z-10" style={{ left: roX }} />
              </>
            );
          })()}
          {/* playhead */}
          <div className="absolute top-0 bottom-0 w-[2px] bg-[var(--color-gold-400)] pointer-events-none z-20"
            style={{ left: playheadX, boxShadow: "0 0 6px rgba(245,196,83,0.8)" }} />
        </div>
      </div>
    </div>
  );
}
